import React from 'react';
import { DoctoralTrajectoryData } from '../types';
import {
  GlobeIcon,
  DownloadIcon,
  XIcon,
  FileTextIcon,
  UserIcon,
} from './DoctoralIcons';

interface DoctoralTrajectoryOverlayProps {
  data?: DoctoralTrajectoryData | null;
  onClose: () => void;
  onOpenJointScience?: () => void;
}

export const DoctoralTrajectoryOverlay: React.FC<DoctoralTrajectoryOverlayProps> = ({
  data,
  onClose,
  onOpenJointScience,
}) => {
  const readiness = data?.dissertation_readiness ?? 62;
  const pubsCount = data?.publications_count ?? 3;
  const jointCount = data?.joint_works_count ?? 4;
  const defenseSteps = data?.defense_steps_count ?? 4;

  const jointWorks = data?.joint_works || [
    {
      id: 'jw-1',
      code: 'JW-01',
      title: 'Сравнительный анализ корпусных методов в тюркологии',
      participants: 'Бекмуратова А., Садыков Б.Т.',
      status_label: 'Опубликовано (Scopus Q2)',
      badge_variant: 'emerald',
    },
    {
      id: 'jw-2',
      code: 'JW-02',
      title: 'Цифровая платформа для морфологического разбора текстов',
      participants: 'Бекмуратова А., Prof. Dr. Michael Weber',
      status_label: 'В рецензировании',
      badge_variant: 'amber',
    },
    {
      id: 'jw-3',
      code: 'JW-03',
      title: 'Методика внедрения электронных корпусов в бакалавриат',
      participants: 'Бекмуратова А., Кафедра казахского языкознания',
      status_label: 'Апробировано в 3 семестре',
      badge_variant: 'cyan',
    },
    {
      id: 'jw-4',
      code: 'JW-04',
      title: 'Грантовый проект АР19678120 МНВО РК',
      participants: 'Научная группа проф. Садыкова Б.Т.',
      status_label: 'Этап 2024/2025 завершен',
      badge_variant: 'emerald',
    },
  ];

  return (
    <div className="cab-overlay">
      <div className="cab-shell cab-shell--wide cab-shell--tall bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
        <div className="p-6 border-b border-slate-800 flex items-center justify-between bg-slate-950/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
              <GlobeIcon className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-white">
                  Научная траектория докторанта (Scientific GPS)
                </h3>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 font-bold border border-cyan-500/30">
                  Карта 14
                </span>
              </div>
              <p className="text-xs text-slate-400">
                Индивидуальный научно-образовательный вектор от зачисления до защиты докторской диссертации
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {onOpenJointScience ? (
              <button
                type="button"
                onClick={onOpenJointScience}
                className="px-3 py-1.5 rounded-xl bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30 text-xs font-semibold transition"
              >
                Совместная научная работа
              </button>
            ) : null}
            <button
              onClick={() => window.print()}
              className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs font-semibold flex items-center gap-1.5 transition"
            >
              <DownloadIcon className="w-3.5 h-3.5" />
              Экспорт траектории
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800 space-y-1">
              <span className="text-[11px] text-slate-400">Готовность диссертации</span>
              <div className="text-xl font-bold text-cyan-400">{readiness}%</div>
              <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
                <div
                  className="bg-cyan-400 h-full rounded-full transition-all"
                  style={{ width: `${readiness}%` }}
                />
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800 space-y-1">
              <span className="text-[11px] text-slate-400">Научные публикации</span>
              <div className="text-xl font-bold text-emerald-400">{pubsCount} статьи</div>
              <span className="text-[11px] text-slate-500">Scopus + КОКСНВО</span>
            </div>

            <div className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800 space-y-1">
              <span className="text-[11px] text-slate-400">Совместные проекты</span>
              <div className="text-xl font-bold text-amber-300">{jointCount} работы</div>
              <span className="text-[11px] text-slate-500">С науч. руководителем</span>
            </div>

            <div className="p-4 rounded-2xl bg-slate-950/60 border border-slate-800 space-y-1">
              <span className="text-[11px] text-slate-400">Шагов до предзащиты</span>
              <div className="text-xl font-bold text-white">{defenseSteps} шага</div>
              <span className="text-[11px] text-slate-500">Дорожная карта кафедры</span>
            </div>
          </div>

          <div className="p-5 rounded-2xl bg-slate-950/60 border border-slate-800 space-y-2">
            <h4 className="text-xs font-bold text-cyan-400 uppercase tracking-wider">
              Текущий фокус траектории (2 курс, 4 семестр)
            </h4>
            <p className="text-sm text-slate-200 leading-relaxed">
              {data?.trajectory_overview ||
                'Завершение исследовательской практики на базе НИИ тюркологии, подготовка текста 3-й статьи для журнала перечня КОКСНВО и согласование дат зарубежной стажировки в Берлине.'}
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-white flex items-center gap-2">
                <FileTextIcon className="w-4 h-4 text-emerald-400" />
                Совместные научные работы и проекты
              </h4>
              <span className="text-xs text-slate-400">Синхронизация с 02.02 Эдвайзер</span>
            </div>

            <div className="space-y-2.5">
              {jointWorks.map((work) => (
                <div
                  key={work.id}
                  className="p-4 rounded-xl bg-slate-950/60 border border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-3"
                >
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-800 px-2 py-0.5 rounded">
                        {work.code}
                      </span>
                      <h5 className="text-xs font-bold text-white">{work.title}</h5>
                    </div>
                    <p className="text-[11px] text-slate-400 flex items-center gap-1.5 pt-0.5">
                      <UserIcon className="w-3 h-3 text-slate-500" />
                      Участники: {work.participants}
                    </p>
                  </div>

                  <span
                    className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap self-start md:self-center ${
                      work.badge_variant === 'emerald'
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                        : work.badge_variant === 'amber'
                        ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                        : 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                    }`}
                  >
                    {work.status_label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
